import React, { useState, useRef } from 'react';
import { makeStyles, Switch, Button } from '@fluentui/react-components';
import {
  ImageEdit20Regular,
  Copy20Regular,
  Delete20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
  MoreHorizontal20Regular,
  ReOrderDotsVertical20Regular,
  Add20Regular,
} from '@fluentui/react-icons';
import { RankingQuestion as RankingQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  questionCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    width: '100%',
    position: 'relative',
  },
  topBar: {
    height: '5px',
    backgroundColor: '#03787c',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingBottom: '10px',
  },
  canvas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    padding: '0 20px',
  },
  actionButtons: {
    display: 'flex',
    gap: '28px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    paddingTop: '16px',
  },
  actionIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    '&:hover': {
      color: '#03787c',
    },
  },
  questionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
  },
  questionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  questionTitleRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  questionTypeDropdown: {
    minWidth: '120px',
    flexShrink: 0,
  },
  questionNumber: {
    width: '20px',
    height: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#292929',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionInput: {
    flex: 1,
    height: '32px',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '6px 40px 6px 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'transparent',
    },
  },
  inputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  imageEditIcon: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
  optionsSection: {
    paddingLeft: '20px',
    paddingRight: '10px',
    width: 'calc(100% - 30px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  optionRow: {
    display: 'flex',
    gap: '0',
    alignItems: 'center',
    height: '32px',
    position: 'relative',
  },
  dragHandle: {
    fontSize: '20px',
    color: '#616161',
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  optionInput: {
    width: '100%',
    height: '32px',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '0',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '0 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'transparent',
    },
  },
  optionInputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  optionActionButtons: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 0.2s',
    paddingLeft: '12px',
  },
  optionActionIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    '&:hover': {
      color: '#03787c',
    },
  },
  addButton: {
    paddingLeft: '0',
    width: '100%',
    '& button': {
      minWidth: 'auto',
      height: '32px',
      padding: '6px 12px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      fontWeight: 400,
      color: '#03787c',
      '& .fui-Button__icon': {
        fontSize: '20px',
        color: '#03787c',
      },
    },
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#e1e1e1',
  },
  settingsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 0 16px',
    height: '44px',
  },
  settingsGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  toggleGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '8px',
  },
  toggleLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#242424',
  },
  moreIcon: {
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
});

// Copilot icon component
const CopilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src="/src/assets/office/ribbon/Copilot.svg" 
    alt="Copilot" 
    className={className}
    style={{ width: '20px', height: '20px' }}
  />
);

interface RankingQuestionProps {
  question: RankingQuestionType;
  questionNumber: number;
  onChange: (question: RankingQuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const RankingQuestion: React.FC<RankingQuestionProps> = ({
  question,
  questionNumber,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const styles = useStyles();
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number | null>(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    onChange({ ...question, title: value });
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...question.options];
    if (typeof newOptions[index] === 'string') {
      newOptions[index] = { id: `option-${index}`, text: value };
    } else {
      newOptions[index] = { ...newOptions[index], text: value };
    }
    onChange({ ...question, options: newOptions });
  };

  const handleDeleteOption = (index: number) => {
    if (question.options.length > 2) {
      const newOptions = question.options.filter((_, i) => i !== index);
      onChange({ ...question, options: newOptions });
    }
  };

  const handleMoveOption = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newOptions = [...question.options];
      [newOptions[index], newOptions[index - 1]] = [newOptions[index - 1], newOptions[index]];
      onChange({ ...question, options: newOptions });
    } else if (direction === 'down' && index < question.options.length - 1) {
      const newOptions = [...question.options];
      [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
      onChange({ ...question, options: newOptions });
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOptions = [...question.options];
    const draggedItem = newOptions[draggedIndex];
    newOptions.splice(draggedIndex, 1);
    newOptions.splice(index, 0, draggedItem);
    
    onChange({ ...question, options: newOptions });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAddOption = () => {
    onChange({ 
      ...question, 
      options: [...question.options, { 
        id: `option-${Date.now()}`, 
        text: `Option ${question.options.length + 1}` 
      }] 
    });
  };

  const handleToggleRequired = () => {
    onChange({ ...question, required: !question.required });
  };

  return (
    <div className={styles.questionCard}>
      <div className={styles.topBar} />
      
      <div className={styles.contentWrapper}>
        <div className={styles.canvas}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <CopilotIcon className={styles.actionIcon} />
            <Copy20Regular className={styles.actionIcon} onClick={onDuplicate} />
            <Delete20Regular className={styles.actionIcon} onClick={onDelete} />
            <ArrowUp20Regular className={styles.actionIcon} onClick={onMoveUp} />
            <ArrowDown20Regular className={styles.actionIcon} onClick={onMoveDown} />
          </div>

          <div className={styles.questionSection}>
            <div className={styles.questionHeader}>
              {/* Question Title */}
              <div className={styles.questionTitleRow}>
                <div className={styles.questionNumber}>{questionNumber}.</div>
                <div className={styles.questionInput}>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={question.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    placeholder="Question"
                  />
                  {isTitleFocused && <div className={styles.inputFocusBorder} />}
                  <ImageEdit20Regular className={styles.imageEditIcon} />
                </div>
              </div>
            </div>

            {/* Options */}
            <div className={styles.optionsSection}>
              {question.options.map((option, index) => {
                const showActions = hoveredOptionIndex === index || focusedOptionIndex === index;
                const optionId = typeof option === 'string' ? `option-${index}` : option.id;
                const optionText = typeof option === 'string' ? option : option.text;
                return (
                  <div 
                    key={optionId} 
                    className={styles.optionRow}
                    onMouseEnter={() => setHoveredOptionIndex(index)}
                    onMouseLeave={() => setHoveredOptionIndex(null)}
                    onDragOver={(e) => handleDragOver(e, index)}
                  >
                    <div 
                      className={styles.dragHandle} 
                      style={{ opacity: showActions ? 1 : 0, cursor: showActions ? 'grab' : 'default' }}
                      draggable={showActions}
                      onDragStart={() => handleDragStart(index)}
                      onDragEnd={handleDragEnd}
                    >
                      <ReOrderDotsVertical20Regular />
                    </div>
                    <div className={styles.optionInput}>
                      <input
                        type="text"
                        value={optionText}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        onFocus={() => setFocusedOptionIndex(index)}
                        onBlur={() => setFocusedOptionIndex(null)}
                        placeholder={`Option ${index + 1}`}
                      />
                      {focusedOptionIndex === index && <div className={styles.optionInputFocusBorder} />}
                    </div>
                    <div className={styles.optionActionButtons} style={{ opacity: showActions ? 1 : 0 }}>
                      <Delete20Regular 
                        className={styles.optionActionIcon}
                        onClick={() => handleDeleteOption(index)}
                      />
                      <ArrowUp20Regular 
                        className={styles.optionActionIcon}
                        onClick={() => handleMoveOption(index, 'up')}
                      />
                      <ArrowDown20Regular 
                        className={styles.optionActionIcon}
                        onClick={() => handleMoveOption(index, 'down')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Option Button */}
            <div className={styles.addButton}>
              <Button
                appearance="transparent"
                icon={<Add20Regular />}
                onClick={handleAddOption}
              >
                Add option
              </Button>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Settings Bar */}
        <div className={styles.settingsBar}>
          <div />
          <div className={styles.settingsGroup}>
            <div className={styles.toggleGroup}>
              <Switch
                checked={question.required}
                onChange={handleToggleRequired}
              />
              <span className={styles.toggleLabel}>Required</span>
            </div>
            <MoreHorizontal20Regular className={styles.moreIcon} />
          </div>
        </div>
      </div>
    </div>
  );
};
